import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useCapabilities } from "@/hooks/useCapabilities";
import type { RoutePlan } from "@/types/routing";

type RoutingAdvisorProps = {
  plan: RoutePlan | null;
  advice: string;
  loading: boolean;
  onGenerate: () => Promise<string>;
};

export function RoutingAdvisor({ plan, advice, loading, onGenerate }: RoutingAdvisorProps) {
  const { capabilities, openUpgradeModal } = useCapabilities();
  const canUseAdvisor = capabilities.canUseRoutingAdvisor;
  const [open, setOpen] = useState(true);

  const handleCopy = async () => {
    if (!advice) return;
    try {
      await navigator.clipboard.writeText(advice);
    } catch {
      // ignore clipboard errors in Phase 1
    }
  };

  const handleRefresh = async () => {
    if (!canUseAdvisor) {
      openUpgradeModal("Routing advisor");
      return;
    }
    await onGenerate();
    setOpen(true);
  };

  return (
    <CapabilityGate
      flag="canUseRoutingAdvisor"
      fallback={
        <LockedFeatureCard
          title="Routing advisor"
          description="Upgrade to Pro for AI summaries that flag gaps, highlight clusters, and suggest next steps."
          onUpgrade={() => openUpgradeModal("Routing advisor")}
        />
      }
    >
      <Card className="aa-glass-card rounded-2xl">
      <CardHeader className="flex items-center justify-between px-6 pt-6 pb-4">
        <CardTitle className="text-lg font-semibold tracking-wide text-foreground/90">Advisor</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen((value) => !value)}>
            {open ? "Collapse" : "Open"}
          </Button>
          <Button size="sm" variant="default" disabled={!plan || loading} onClick={handleRefresh}>
            {loading ? "Thinking…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      {open ? (
        <CardContent className="space-y-3 px-6 pb-6 text-sm text-foreground/80">
          {advice ? (
            <>
              <p className="whitespace-pre-line rounded-xl border border-border bg-background/40 p-4 text-sm leading-relaxed text-foreground">
                {advice}
              </p>
              <Button size="sm" variant="secondary" className="w-full" onClick={handleCopy}>
                Copy summary
              </Button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Generate advice once a route is available.</p>
          )}
        </CardContent>
      ) : null}
      </Card>
    </CapabilityGate>
  );
}
